import { sentryModel } from '@/shared/model';

/**
 * Type-only reference to the widget element. Using an `import(...)` type query
 * (rather than a static `import`) guarantees no runtime import is emitted, so
 * the ~2MB bundle is pulled in solely by the dynamic `import()` below and stays
 * in its own lazy chunk.
 */
type FreedomPrimeWidgetElement =
  import('@freedom-lifestyle/freedom-prime-widget').FreedomPrimeWidget;

const WIDGET_TAG = 'freedom-prime-widget';

let widgetModulePromise: Promise<unknown> | null = null;

/**
 * Lazily loads the Freedom Prime widget bundle (~2MB). It is kept out of the
 * initial chunk and fetched only the first time the user opens the modal;
 * later opens reuse the resolved module.
 */
const loadWidgetModule = (): Promise<unknown> => {
  if (!widgetModulePromise) {
    widgetModulePromise = import('@freedom-lifestyle/freedom-prime-widget');
  }

  return widgetModulePromise;
};

interface OpenFreedomPrimeWidgetOptions {
  /** UI locale forwarded to the widget (ru | kk | en). */
  locale: string;
  /** Overrides theme auto-detection; defaults to the app's current theme. */
  theme?: 'light' | 'dark';
  /** Optional Freedom Prime user identifier. */
  uns?: string | null;
  /** Optional promocode to prefill in the widget. */
  promocode?: string | null;
}

/**
 * Opens the Freedom Prime subscription widget as a modal overlay. The widget is
 * a Lit web component that authenticates via `window.FreedomPrime.fetchToken`
 * (wired in `app/providers/with-freedom-prime.ts`), so no token is passed here.
 *
 * The element is created imperatively and appended to `document.body` — like any
 * overlay it lives outside Vue's vdom, which also avoids registering a custom
 * element in the Vue template compiler.
 */
export const openFreedomPrimeWidget = async (
  options: OpenFreedomPrimeWidgetOptions,
): Promise<void> => {
  try {
    await loadWidgetModule();
    await customElements.whenDefined(WIDGET_TAG);
  } catch (error) {
    sentryModel.captureException(
      sentryModel.getReadableError(
        'ERR_FAILED_TO_LOAD_FREEDOM_PRIME_WIDGET',
        error,
      ),
    );

    return;
  }

  // Reuse a live instance if one is already mounted so rapid re-clicks don't
  // stack multiple overlays on the page.
  const existingWidget =
    document.querySelector<FreedomPrimeWidgetElement>(WIDGET_TAG);
  if (existingWidget) {
    existingWidget.open();

    return;
  }

  const widget = document.createElement(
    WIDGET_TAG,
  ) as FreedomPrimeWidgetElement;
  widget.locale = options.locale;
  widget.theme =
    options.theme ??
    (document.documentElement.classList.contains('dark') ? 'dark' : 'light');

  if (options.uns != null) {
    widget.uns = options.uns;
  }

  if (options.promocode != null) {
    widget.promocode = options.promocode;
  }

  // The widget dispatches `close-widget` when dismissed; tear the element down
  // so detached overlays don't leak across opens.
  const handleClose = () => {
    widget.removeEventListener('close-widget', handleClose);
    widget.remove();
  };

  widget.addEventListener('close-widget', handleClose);

  document.body.appendChild(widget);
  widget.open();
};
